import { startTransition, useCallback, useEffect } from 'react';

import { useHasPermission } from '@/auth';
import useApiErrorStatus from '@/hooks/useApiErrorStatus';
import useVervangStamgegevens from '@/state/selectors/useVervangStamgegevens';

import { useFetchWithAuth } from '../useFetchWithAuth';

/**
 * Custom hook voor het periodiek updaten van stamgegeven in de clientStore.
 * Let op: Gebruik deze hook maar op 1 plek binnen een route, omdat anders dubbele updates
 * en onnodige network-calls worden uitgevoerd en onnodige rerendering plaatsvindt.
 */
export const useUpdateStamgegevensInStore = () => {
  const isAllowedToView = useHasPermission('platform.view');
  const { fetchWithAuth } = useFetchWithAuth();
  const { vervangAlleDienstregelpunten, vervangAlleMaterieelTypen } = useVervangStamgegevens();
  const { handleApiStatus } = useApiErrorStatus();

  const fetchDienstregelpunten = useCallback(async () => {
    const response = await fetchWithAuth('/stamgegevens/drglptnamen');
    if (response.status > 499) {
      throw new Error(`Serverfout ${response.status}: ${response.statusText}`);
    } else if (!response.ok) {
      throw new Error(`Clientfout ${response.status}: ${response.statusText}`);
    } else {
      return (await response.json()) as string[];
    }
  }, [fetchWithAuth]);

  const fetchMaterieeltypen = useCallback(async () => {
    const response = await fetchWithAuth('/stamgegevens/materieeltypen');
    if (response.status > 499) {
      throw new Error(`Serverfout ${response.status}: ${response.statusText}`);
    } else if (!response.ok) {
      throw new Error(`Clientfout ${response.status}: ${response.statusText}`);
    } else {
      return (await response.json()) as string[];
    }
  }, [fetchWithAuth]);

  const verversAlleStamgegevens = useCallback(() => {
    (async () => {
      try {
        const dienstregelpunten = await fetchDienstregelpunten();
        const materieelTypen = await fetchMaterieeltypen();
        vervangAlleDienstregelpunten(dienstregelpunten);
        vervangAlleMaterieelTypen(materieelTypen);
        handleApiStatus('verversStamgegevens', false); // reset the api banner for this request if successfull again.
      } catch (error) {
        console.error(error);
        handleApiStatus('verversStamgegevens', true); // show the api banner for this request.
      }
    })();
  }, [fetchDienstregelpunten, fetchMaterieeltypen, vervangAlleDienstregelpunten, vervangAlleMaterieelTypen]);

  useEffect(() => {
    // Roep de functie initieel altijd aan om de cache te vullen:
    if (isAllowedToView) {
      startTransition(() => {
        verversAlleStamgegevens();
      });
    }

    // En zorg ervoor dat de gegevens ook up-to-date blijven door elk kwartier een refresh uit te voeren:
    const refreshInterval = setInterval(() => {
      startTransition(() => {
        verversAlleStamgegevens();
      });
    }, 900_000);
    return () => {
      clearInterval(refreshInterval);
    };
  }, [verversAlleStamgegevens, isAllowedToView]);

  // Deze hook heeft geen return value, maar alleen een side-effect i.e. het updaten van de clientState
};
